/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */
import {
  AbsoluteTime,
  Amounts,
  HttpStatusCode,
  TalerError,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  Attention,
  Loading,
  RenderAmount,
  RouteDefinition,
  Time,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { VNode, h } from "preact";

import {
  revalidateCashoutDetails,
  revalidateConversionInfo,
  useCashoutDetails,
  useConversionInfo,
} from "../../hooks/regional.js";
import { RetryableError } from "../../components/RetryableError.js";

const TALER_SCREEN_ID = 128;

interface Props {
  id: string;
  routeClose: RouteDefinition;
}

ShowCashoutDetails.SCREEN_ID = TALER_SCREEN_ID;
export function ShowCashoutDetails({ id, routeClose }: Props): VNode {
  const { i18n } = useTranslationContext();
  const cid = Number.parseInt(id, 10);

  const result = useCashoutDetails(Number.isNaN(cid) ? undefined : cid);
  const info = useConversionInfo();

  if (Number.isNaN(cid)) {
    return (
      <Attention type="danger" title={i18n.str`Cashout ID must be a number.`} />
    );
  }
  if (!result) {
    return <Loading />;
  }
  if (result instanceof TalerError) {
    return (
      <RetryableError
        error={result}
        title={i18n.str`Failed to load cashout details.`}
        onRetry={() => void revalidateCashoutDetails()}
      />
    );
  }
  if (result.type === "fail") {
    switch (result.case) {
      case HttpStatusCode.NotFound:
        return (
          <Attention
            type="warning"
            title={i18n.str`This cashout was not found. It may already have been aborted.`}
          ></Attention>
        );
      case HttpStatusCode.NotImplemented:
        return (
          <Attention type="warning" title={i18n.str`Cashout is disabled`}>
            <i18n.Translate>
              Cashout should be enabled in the configuration, the conversion
              rate should be initialized with fee(s), rates and a rounding mode.
            </i18n.Translate>
          </Attention>
        );
      default:
        assertUnreachable(result);
    }
  }
  if (!info) {
    return <Loading />;
  }

  if (info instanceof TalerError) {
    return (
      <RetryableError
        error={info}
        title={i18n.str`Failed to load conversion rate information.`}
        onRetry={() => void revalidateConversionInfo()}
      />
    );
  }
  if (info.type === "fail") {
    switch (info.case) {
      case HttpStatusCode.NotImplemented: {
        return (
          <Attention type="danger" title={i18n.str`Cashout is disabled`}>
            <i18n.Translate>
              Cashout should be enabled in the configuration, the conversion
              rate should be initialized with fee(s), rates and a rounding mode.
            </i18n.Translate>
          </Attention>
        );
      }
      default:
        assertUnreachable(info);
    }
  }

  const { fiat_currency_specification, regional_currency_specification } =
    info.body;

  return (
    <section class="mt-2">
      <a
        href={routeClose.url({})}
        name="close"
        class="inline-flex items-center text-sm font-semibold text-brand hover:underline"
      >
        <span class="mr-1" aria-hidden="true">
          ←
        </span>
        <i18n.Translate>Back to cashout history</i18n.Translate>
      </a>
      <div class="mx-auto mt-6 max-w-2xl">
        <h1 class="text-2xl font-semibold text-brand">
          <i18n.Translate>Cashout details</i18n.Translate>
        </h1>
        <p class="mt-2 text-sm text-gray-600">
          <i18n.Translate>
            Amounts recorded for this completed cashout transfer.
          </i18n.Translate>
        </p>
        <dl class="mt-6 divide-y divide-onBackground/10 overflow-hidden rounded-xl border border-onBackground/10 bg-white px-5 shadow-sm sm:px-8">
          <div class="flex items-start justify-between gap-6 py-4">
            <dt class="text-sm text-gray-600">
              <i18n.Translate>Transfer reference</i18n.Translate>
            </dt>
            <dd class="text-right text-sm font-medium text-onBackground">
              {result.body.subject}
            </dd>
          </div>
          {result.body.creation_time.t_s !== "never" ? (
            <div class="flex items-center justify-between gap-6 py-4">
              <dt class="text-sm text-gray-600">
                <i18n.Translate>Date</i18n.Translate>
              </dt>
              <dd class="text-right text-sm text-onBackground">
                <Time
                  format="dd/MM/yyyy HH:mm:ss"
                  timestamp={AbsoluteTime.fromProtocolTimestamp(
                    result.body.creation_time,
                  )}
                />
              </dd>
            </div>
          ) : undefined}
          <div class="flex items-center justify-between gap-6 py-4">
            <dt class="text-sm text-gray-600">
              <i18n.Translate>Amount debited</i18n.Translate>
            </dt>
            <dd class="font-medium text-onBackground">
              <RenderAmount
                value={Amounts.parseOrThrow(result.body.amount_debit)}
                negative
                withSign
                withColor
                spec={regional_currency_specification}
              />
            </dd>
          </div>
          <div class="flex items-center justify-between gap-6 py-4">
            <dt class="text-sm text-gray-600">
              <i18n.Translate>Amount transferred</i18n.Translate>
            </dt>
            <dd class="font-semibold text-onBackground">
              <RenderAmount
                value={Amounts.parseOrThrow(result.body.amount_credit)}
                withColor
                spec={fiat_currency_specification}
              />
            </dd>
          </div>
        </dl>
      </div>
    </section>
  );
}
